import { FC, Fragment, useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { FieldTable2 } from 'components/_basic/Form/FieldTable2';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import {
  initialPeriodsTableRow,
  IPeriodsTableRow,
  IPeriodsTableRows,
} from 'components/BusinessToolForm/constants/periods';
import {
  INITIAL_LIABILITIES_NAMES,
  NON_CURRENT_LIABILITIES_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialLiabilitiesForm/utils/form';
import { initialLiabilitiesFormPrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import {
  computeConditionalBorrowed,
  computeConditionalRepayment,
} from 'components/BusinessToolForm/utils/financialCalculations';
import {
  calculatePeriodValueBaseOnPreviousPeriod,
  PERIODS_OPERATION_TYPES,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ICalendarPlanFormProps } from './types';
import { CALENDAR_PLAN_NAMES } from './utils/form';
import { FullScreenWrapper } from '../../../components/FullScreenWrapper';
import { useUnitChange } from '../../../hooks/useUnitChange';
import { FINANCING_ACTIVITIES_NAMES } from '../../../ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/FinancingActivities/utils/form';
import { financingActivitiesPrefix } from '../../../ProjectedCashFlow/utils/form';
import { calendarPlanPrefix, FIRST_COLUMN_WIDTH, loanRepaymentSchedulePrefix } from '../../utils/form';
import { PeriodFormHeader } from '../components/PeriodFormHeader';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from '../LoanRepaymentScheduleForm/utils/form';

export const CalendarPlanForm: FC<ICalendarPlanFormProps> = ({ disabled }) => {
  const { t } = useTranslation();
  const { key } = useUnitChange();
  const { excessiveBorrowedFundsPeriods, setExcessiveBorrowedFundsPeriods, setExcessiveLoanRepaymentPeriods } =
    useFinancialWarnings();

  const { control, getValues, setValue } = useFormContext();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const columnsNames = getPeriodColumnsNames(period);

  const tableName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TABLE);
  const totalName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TOTAL);

  const { fields } = useFieldArray({ control, name: tableName });
  const calendarPlanTable = useWatch({ name: tableName });

  const variantsName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.VARIANTS, ':index');

  const issuanceDebtField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT);
  const repaymentDebtField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT);
  const issuanceEquityField = concatWithDot(
    financingActivitiesPrefix,
    FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY,
  );

  const loanRepaymentTableName = concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE);
  const loanRepaymentTable = useWatch({ name: loanRepaymentTableName });

  const initialLongTermDebtName = concatWithDot(
    initialLiabilitiesFormPrefix,
    INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
    NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT,
  );
  const initialLongTermDebt = useWatch({ name: initialLongTermDebtName });

  const combinedHandler = useCallback(
    (values: IPeriodsTableRows[]) => {
      const ownFunds = values[0];
      const borrowedFunds = values[1];
      const investmentFunds = values[2];
      const tableTotal = summarizeSubtractPeriodValues(values, PERIODS_OPERATION_TYPES.ADD);

      const issuanceEquity = summarizeSubtractPeriodValues([ownFunds, investmentFunds], PERIODS_OPERATION_TYPES.ADD);

      const repaymentFunds = (getValues(loanRepaymentTableName)?.[0] ?? loanRepaymentTable?.[0]) as
        | IPeriodsTableRows
        | undefined;
      const openingLongTermDebt = getValues(initialLongTermDebtName) ?? initialLongTermDebt;

      if (!repaymentFunds) {
        setValue(issuanceDebtField, borrowedFunds);
        setValue(repaymentDebtField, initialPeriodsTableRow);
        setValue(issuanceEquityField, issuanceEquity);
        setValue(totalName, tableTotal);
        setExcessiveBorrowedFundsPeriods(new Set());
        setExcessiveLoanRepaymentPeriods(new Set());
        return;
      }

      const netChange = summarizeSubtractPeriodValues(
        [borrowedFunds, repaymentFunds],
        PERIODS_OPERATION_TYPES.SUBTRACT,
      );
      const controlParam = calculatePeriodValueBaseOnPreviousPeriod(
        netChange as unknown as IPeriodsTableRow,
        String(Number(openingLongTermDebt) || 0),
      );

      const { conditionalBorrowed, excessivePeriodKeys: excessiveBorrowedPeriodKeys } = computeConditionalBorrowed(
        controlParam,
        borrowedFunds as unknown as IPeriodsTableRow,
      );
      const { conditionalRepaymentDebt, excessivePeriodKeys: excessiveRepaymentPeriodKeys } =
        computeConditionalRepayment(controlParam, repaymentFunds as unknown as IPeriodsTableRow);

      setExcessiveBorrowedFundsPeriods(excessiveBorrowedPeriodKeys);
      setExcessiveLoanRepaymentPeriods(excessiveRepaymentPeriodKeys);
      setValue(issuanceDebtField, conditionalBorrowed as unknown as IPeriodsTableRows);
      setValue(repaymentDebtField, conditionalRepaymentDebt as unknown as IPeriodsTableRows);
      setValue(issuanceEquityField, issuanceEquity);
      setValue(totalName, tableTotal);
    },
    [
      getValues,
      initialLongTermDebt,
      initialLongTermDebtName,
      issuanceDebtField,
      issuanceEquityField,
      loanRepaymentTable,
      loanRepaymentTableName,
      repaymentDebtField,
      setExcessiveBorrowedFundsPeriods,
      setExcessiveLoanRepaymentPeriods,
      setValue,
      totalName,
    ],
  );

  useDebouncedSave({
    data: { calendarPlanTable, loanRepaymentTable, initialLongTermDebt },
    saveFunction: ({ calendarPlanTable: values }) => combinedHandler(values),
    debounceTime: 500,
  });

  useEffect(() => {
    if (calendarPlanTable?.length) {
      combinedHandler(calendarPlanTable);
    }
  }, [calendarPlanTable, loanRepaymentTable, initialLongTermDebt, combinedHandler]);

  return (
    <FormSectionCard collapsible title={t('financialPlan.cashFlowElements.calendarPlan.title')} disabled={disabled}>
      <FullScreenWrapper title={t('financialPlan.cashFlowElements.calendarPlan.title')} disabled={disabled}>
        <Fragment key={key}>
          <PeriodFormHeader
            disabled={disabled}
            periodId="calendarPlanFormHeader.period"
            currencyUnitName={concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.CURRENCY)}
          />
          <FormSectionFilledCard>
            <FieldTable2
              showSumRow
              name={tableName}
              totalName={totalName}
              headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
              columnsNames={columnsNames || []}
              rows={fields}
              disabled={disabled}
              watchRowTitleName={variantsName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              recalculateOnChangeRowIndexes={[1]}
              highlightedColumnsByRow={
                excessiveBorrowedFundsPeriods.size > 0 ? new Map([[1, excessiveBorrowedFundsPeriods]]) : undefined
              }
              highlightedColumnsTooltipByRow={
                excessiveBorrowedFundsPeriods.size > 0
                  ? new Map([
                      [
                        1,
                        t('financialPlan.cashFlowElements.calendarPlan.excessiveBorrowedFundsTooltip'),
                      ],
                    ])
                  : undefined
              }
            />
          </FormSectionFilledCard>
        </Fragment>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
